
import React, { useCallback, memo } from "react";
import { CalendarEvent } from "@/hooks/calendar/types";
import { EventItem } from "./timeslot/EventItem";
import { AddButton } from "./timeslot/AddButton";
import { UnavailableOverlay } from "./timeslot/UnavailableOverlay";

interface CalendarTimeSlotProps {
  date: Date;
  hour: number;
  isToday: boolean;
  isUnavailable: boolean;
  isInPast: boolean;
  events: CalendarEvent[];
  onTimeSlotClick: (date: Date, hour: number) => void;
  onDeleteEvent: ((event: CalendarEvent) => Promise<void>) | undefined;
  isLastColumn: boolean;
}

const CalendarTimeSlot: React.FC<CalendarTimeSlotProps> = memo(({
  date,
  hour,
  isToday,
  isUnavailable,
  isInPast,
  events,
  onTimeSlotClick,
  onDeleteEvent,
  isLastColumn
}) => {
  const isEmpty = events.length === 0;

  const handleAddButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onTimeSlotClick(date, hour);
  }, [date, hour, onTimeSlotClick]);

  const renderAddButton = () => {
    if (!isUnavailable && onTimeSlotClick) {
      return (
        <AddButton onClick={handleAddButtonClick} />
      );
    }
    return null;
  };

  return (
    <div 
      className={`p-1 sm:p-2 border-r min-h-[90px] sm:min-h-[100px] relative 
        ${isToday ? "bg-primary/5" : ""} 
        ${isInPast ? "bg-neutral-100/50" : ""}
        ${isLastColumn ? "border-r-0" : ""}
        cursor-default`} 
    >
      {events.map(event => (
        <EventItem
          key={event.id}
          event={event}
          isInPast={isInPast}
          onDeleteEvent={onDeleteEvent}
        />
      ))}
      
      {renderAddButton()}
      
      <UnavailableOverlay isUnavailable={isUnavailable} />
    </div>
  );
});

CalendarTimeSlot.displayName = "CalendarTimeSlot";

export default CalendarTimeSlot;
